import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import "./fix.css";
import { useCallback, useMemo, useRef, useState } from "react";
import { MentionPicker, MentionUser } from "./MentionPicker";
import { MENTION_BLOT_NAME, registerMentionBlot } from "./mentionBlot";
import { insertMention } from "./mentionInsert";
import { findMentionTrigger, MentionTrigger } from "./mentionTrigger";
import { TABLE_CLIPBOARD_MATCHERS } from "./tableClipboard";

// The mention format has to exist before any editor renders a note that contains one,
// otherwise Quill drops the chip while parsing the stored HTML.
registerMentionBlot(Quill);

export interface MentionsConfig {
  /** Candidates for the open picker — normally the result of querying the live query. */
  users: MentionUser[];
  /** The typed query as it changes, and null once the picker closes. */
  onQueryChange: (query: string | null) => void;
  loading?: boolean;
  /** True when the candidate lookup failed, so a failure never reads as "no matches". */
  loadFailed?: boolean;
}

interface RCGReactQuillInputProps {
  toolbarId: string | number;
  toolbarStyle?: React.CSSProperties;
  placeholder?: string;
  textValue: string | undefined;
  onChange: (content: any, delta: any, source: any) => void;
  style?: React.CSSProperties;
  /** Enables @-mention insertion. Omit to get an editor with no mention support. */
  mentions?: MentionsConfig;
}

const ALLOWED_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "color",
  "background",
  "list",
  "indent",
  // Quill's built-in table format. Whitelisting it pulls in the row/body/container
  // blots too (via requiredContainer), which is what lets a table pasted from Sirius
  // Chat, Excel or Word land as a table instead of one cell per line.
  "table",
  MENTION_BLOT_NAME,
];

// Brand palette (shared/frontend/src/legacyStyles/base/colors.scss) instead of
// Quill's generic default swatches, so the picker matches the rest of the app.
const TEXT_COLORS = [
  undefined, // "Automatic" — clears the color format, falls back to the default text color
  "#1a1a1a",
  "#07364e", // $blue-900
  "#007dc5", // $blue-600
  "#0091f9", // $blue-500
  "#269fd7", // $blue-400
  "#385e72", // $blue-grey-800
  "#2a3fb3", // $purple-700
  "#20b2aa", // $green-500
  "#4d9078", // $green-700
  "#ff6900", // $orange-500
  "#ed205e", // $pink-700
  "#ec3700", // $red-300
  "#ffffff",
];

const BACKGROUND_COLORS = [
  undefined, // "Automatic" — clears the background, falls back to transparent/white
  "#ffff00", // classic highlighter yellow
  "#f0f0f0", // $grey-50
  "#ebebeb", // $grey-100
  "#dbe2e5", // $blue-grey-50
  "#e7ebee", // $blue-grey-75
  "#cce0f5",
  "#facccc",
];

const ColorOptions = ({ colors }: { colors: (string | undefined)[] }) => (
  <>
    {colors.map((color) => (
      <option key={color ?? "default"} value={color}></option>
    ))}
  </>
);

export const CustomToolbar = ({
  id,
  style = {} as React.CSSProperties,
}: {
  id: string | number;
  style?: React.CSSProperties;
}) => (
  <div id={`toolbar-${id}`} style={style as any}>
    <span className="ql-formats">
      <select className="ql-header" defaultValue="">
        <option value="1"></option>
        <option value="2"></option>
        <option value=""></option>
      </select>
    </span>
    <span className="ql-formats">
      <button className="ql-bold"></button>
      <button className="ql-italic"></button>
      <button className="ql-underline"></button>
    </span>
    <span className="ql-formats">
      <select className="ql-color">
        <ColorOptions colors={TEXT_COLORS} />
      </select>
      <select className="ql-background">
        <ColorOptions colors={BACKGROUND_COLORS} />
      </select>
    </span>
    <span className="ql-formats">
      <button className="ql-list" value="ordered"></button>
      <button className="ql-list" value="bullet"></button>
      <button className="ql-indent" value="-1"></button>
      <button className="ql-indent" value="+1"></button>
    </span>
    <span className="ql-formats">
      <button className="ql-clean"></button>
    </span>
  </div>
);

const EMPTY_STYLE = {};
export function RCGReactQuillInput(props: RCGReactQuillInputProps) {
  const {
    toolbarId,
    toolbarStyle,
    placeholder,
    textValue,
    onChange,
    style,
    mentions,
  } = props;
  const { maxHeight, ...otherStyles } = style || {};

  const quillRef = useRef<ReactQuill>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  // react-quill writes `value` over the editor whenever it differs from the
  // last text the editor reported. A render React runs ahead of the parent's
  // update (an Apollo store update is one) brings the parent's text from
  // before the latest keystroke, so the keystroke was erased: CI saved "A note"
  // as "note". While the parent still holds the text it had before the
  // editor's latest report, keep the editor's own; any other text the parent
  // passes (cleared after a save, a note loaded) is applied.
  const parentText = textValue || "";
  const parentTextRef = useRef(parentText);
  parentTextRef.current = parentText;
  const behind = useRef<{ parentText: string; editorText: string } | null>(null);
  if (behind.current && parentText !== behind.current.parentText) behind.current = null;
  const value = behind.current ? behind.current.editorText : parentText;
  const [trigger, setTrigger] = useState<MentionTrigger | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const mentionUsers = mentions?.users ?? [];
  const pickerOpen = !!mentions && !!trigger;

  const scrollBlockStyle = useMemo(() => {
    return { maxHeight: maxHeight || "600px", overflow: "auto" };
  }, [maxHeight]);

  const modules = useMemo(() => {
    return {
      toolbar: `#toolbar-${toolbarId}`,
      // Evens up ragged rows in pasted tables and supplies the row/column APIs.
      table: true,
      clipboard: {
        matchers: TABLE_CLIPBOARD_MATCHERS,
        style: {
          position: "fixed !important",
          left: "50% !important",
          top: "50% !important",
          display: "none",
        },
      },
    };
  }, [toolbarId]);

  const closePicker = useCallback(() => {
    setTrigger(null);
    setHighlightedIndex(0);
    mentions?.onQueryChange(null);
  }, [mentions]);

  /**
   * Re-reads the text before the cursor after every change, so the picker opens on "@"
   * and closes as soon as the cursor leaves the mention being typed.
   */
  const refreshTrigger = useCallback(() => {
    if (!mentions) return;

    const editor = quillRef.current?.getEditor();
    const selection = editor?.getSelection();
    // No collapsed cursor means there is no mention being typed.
    if (!editor || !selection || selection.length > 0) {
      if (trigger) closePicker();
      return;
    }

    const next = findMentionTrigger(
      editor.getText(0, selection.index),
      selection.index
    );

    if (!next) {
      if (trigger) closePicker();
      return;
    }

    if (next.query !== trigger?.query) {
      setHighlightedIndex(0);
      mentions.onQueryChange(next.query);
    }
    setTrigger(next);
  }, [closePicker, mentions, trigger]);

  const handleChange = useCallback(
    (content: any, delta: any, source: any) => {
      behind.current = {
        parentText: behind.current?.parentText ?? parentTextRef.current,
        editorText: content,
      };
      onChange(content, delta, source);
      refreshTrigger();
    },
    [onChange, refreshTrigger]
  );

  const selectMention = useCallback(
    (user: MentionUser) => {
      const editor = quillRef.current?.getEditor();
      if (!editor || !trigger) return;

      insertMention(editor, trigger, user);
      closePicker();
    },
    [closePicker, trigger]
  );

  /**
   * Runs in the capture phase so the picker gets arrow keys and Enter before Quill's
   * own keyboard bindings see them.
   */
  const handleKeyDownCapture = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!pickerOpen) return;

      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        closePicker();
        return;
      }

      if (!mentionUsers.length) return;

      if (event.key === "ArrowDown") {
        event.preventDefault();
        event.stopPropagation();
        setHighlightedIndex(index => (index + 1) % mentionUsers.length);
        return;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        event.stopPropagation();
        setHighlightedIndex(
          index => (index - 1 + mentionUsers.length) % mentionUsers.length
        );
        return;
      }

      if (event.key === "Enter" || event.key === "Tab") {
        const user = mentionUsers[highlightedIndex];
        if (!user) return;
        event.preventDefault();
        event.stopPropagation();
        selectMention(user);
      }
    },
    [closePicker, highlightedIndex, selectMention, mentionUsers, pickerOpen]
  );

  return (
    <>
      <CustomToolbar id={toolbarId} style={toolbarStyle || {}} />
      <div className={"scroll-block" + toolbarId} style={scrollBlockStyle}>
        <div
          id={`scrolling-container-${toolbarId}`}
          ref={anchorRef}
          onKeyDownCapture={mentions ? handleKeyDownCapture : undefined}
        >
          <ReactQuill
            ref={quillRef}
            theme="snow"
            modules={modules}
            bounds={`#scrolling-container-${toolbarId}`}
            style={{ color: "#1a1a1a", ...(otherStyles || EMPTY_STYLE) } as any}
            placeholder={placeholder ?? ""}
            value={value}
            formats={ALLOWED_FORMATS}
            onChange={handleChange}
            onChangeSelection={mentions ? refreshTrigger : undefined}
          ></ReactQuill>
        </div>
      </div>
      {mentions && (
        <MentionPicker
          open={pickerOpen}
          anchorEl={anchorRef.current}
          users={mentionUsers}
          loading={mentions.loading}
          loadFailed={mentions.loadFailed}
          highlightedIndex={highlightedIndex}
          onHighlight={setHighlightedIndex}
          onSelect={selectMention}
        />
      )}
    </>
  );
}
